import { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2, Check, ChevronLeft, ChevronRight, Sparkles, RotateCcw } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/* Voice-Price Capture — pick an item, tap mic, speak the price,
   we transcribe + parse + save. Walks the owner through all items
   with no price set. Saves a LOT of manual typing for big catalogs. */
export default function VoicePriceDialog({ open, onOpenChange, sarees = [], onDone }) {
    // Items that still need a price (>0). Snapshot when dialog opens so the
    // owner can confirm one before moving to next without the list re-shuffling.
    const [queue, setQueue] = useState([]);
    const [idx, setIdx] = useState(0);
    const [recording, setRecording] = useState(false);
    const [transcribing, setTranscribing] = useState(false);
    const [transcript, setTranscript] = useState("");
    const [parsedPrice, setParsedPrice] = useState(null);
    const [manualPrice, setManualPrice] = useState("");
    const [saving, setSaving] = useState(false);
    const [done, setDone] = useState([]);  // saree ids successfully saved
    const recRef = useRef(null);
    const chunksRef = useRef([]);
    const streamRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const q = sarees.filter((s) => !Number(s.price));
        setQueue(q);
        setIdx(0);
        setTranscript(""); setParsedPrice(null); setManualPrice(""); setDone([]);
    }, [open, sarees]);

    useEffect(() => () => stopStream(), []);

    const current = queue[idx];

    const stopStream = () => {
        const s = streamRef.current;
        if (s) s.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
    };

    const startRec = async () => {
        if (recording) return;
        setTranscript(""); setParsedPrice(null); setManualPrice("");
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            // Prefer audio/webm;codecs=opus — broadly supported and small
            const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
                ? "audio/webm;codecs=opus"
                : MediaRecorder.isTypeSupported("audio/webm")
                    ? "audio/webm"
                    : "";
            const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
            recRef.current = mr;
            chunksRef.current = [];
            mr.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
            mr.onstop = async () => {
                stopStream();
                const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
                await uploadAudio(blob);
            };
            mr.start();
            setRecording(true);
            // Auto-stop after 8s to keep audio short and Whisper cost low
            setTimeout(() => {
                if (recRef.current && recRef.current.state === "recording") {
                    try { recRef.current.stop(); } catch { /* ignore */ }
                    setRecording(false);
                }
            }, 8000);
        } catch (e) {
            toast.error("Microphone access denied — please allow mic permission");
            stopStream();
            setRecording(false);
        }
    };

    const stopRec = () => {
        if (recRef.current && recRef.current.state === "recording") {
            try { recRef.current.stop(); } catch { /* ignore */ }
        }
        setRecording(false);
    };

    const uploadAudio = async (blob) => {
        setTranscribing(true);
        try {
            const fd = new FormData();
            fd.append("audio", blob, "price.webm");
            const { data } = await api.post("/admin/voice-price/transcribe", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 30000,
            });
            setTranscript(data.transcript || "");
            setParsedPrice(data.price);
            if (data.price) {
                setManualPrice(String(data.price));
            }
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not transcribe — try again");
        } finally {
            setTranscribing(false);
        }
    };

    const savePrice = async () => {
        if (!current) return;
        const price = Number(manualPrice);
        if (!price || price < 1) {
            toast.error("Enter or speak a valid price");
            return;
        }
        setSaving(true);
        try {
            await api.patch(`/admin/sarees/${current.id}`, { price });
            setDone((d) => [...d, current.id]);
            toast.success(`₹${price.toLocaleString("en-IN")} saved for ${current.title}`);
            // Auto-advance to next item
            setTranscript(""); setParsedPrice(null); setManualPrice("");
            if (idx < queue.length - 1) {
                setIdx(idx + 1);
            } else {
                toast.success("All items priced!");
                onDone?.();
                onOpenChange(false);
            }
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const skip = () => {
        setTranscript(""); setParsedPrice(null); setManualPrice("");
        if (idx < queue.length - 1) setIdx(idx + 1);
        else { onDone?.(); onOpenChange(false); }
    };

    const goBack = () => {
        setTranscript(""); setParsedPrice(null); setManualPrice("");
        if (idx > 0) setIdx(idx - 1);
    };

    if (!open) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-brand-paper border-brand-border max-w-md" data-testid="voice-price-dialog">
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl flex items-center gap-2">
                        <Mic className="w-5 h-5 text-brand-primary" strokeWidth={1.5} /> Voice price
                    </DialogTitle>
                    <DialogDescription className="text-brand-muted">
                        Speak the price in English or Hindi — we'll transcribe and assign it for you.
                    </DialogDescription>
                </DialogHeader>

                {queue.length === 0 ? (
                    <div className="py-8 text-center">
                        <Check className="w-10 h-10 mx-auto text-brand-primary" strokeWidth={1.2} />
                        <div className="font-serif text-xl mt-4">All items already priced</div>
                        <p className="text-sm text-brand-muted mt-1">
                            Use this tool whenever you upload new items without a price.
                        </p>
                    </div>
                ) : !current ? null : (
                    <div className="space-y-4">
                        <div className="ayraa-overline text-center" data-testid="voice-price-progress">
                            Item {idx + 1} of {queue.length} · {done.length} saved
                        </div>

                        <div className="ayraa-card p-3 flex items-center gap-3" data-testid="voice-price-current">
                            <div className="w-16 h-20 rounded-md overflow-hidden bg-brand-subtle shrink-0">
                                {current.images?.[0] ? (
                                    <img src={current.images[0]} alt={current.title} className="w-full h-full object-cover" />
                                ) : null}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-serif text-base line-clamp-1">{current.title}</div>
                                <div className="text-xs text-brand-muted">{current.fabric || current.product_type || "Item"}</div>
                                <div className="text-xs text-brand-soft mt-1">Currently ₹{Number(current.price) || 0}</div>
                            </div>
                        </div>

                        <div className="grid place-items-center py-2">
                            <button
                                type="button"
                                className={`relative w-24 h-24 rounded-full grid place-items-center transition-all ${
                                    recording
                                        ? "bg-brand-primary text-white shadow-[0_0_0_8px_rgba(128,0,32,0.18)] animate-pulse"
                                        : "bg-brand-subtle text-brand-primary hover:bg-brand-primary hover:text-white"
                                }`}
                                onClick={recording ? stopRec : startRec}
                                disabled={transcribing || saving}
                                data-testid="voice-price-mic"
                                aria-label={recording ? "Stop recording" : "Start recording"}
                            >
                                {transcribing ? (
                                    <Loader2 className="w-10 h-10 animate-spin" strokeWidth={1.2} />
                                ) : recording ? (
                                    <Square className="w-10 h-10" strokeWidth={1.5} fill="currentColor" />
                                ) : (
                                    <Mic className="w-10 h-10" strokeWidth={1.4} />
                                )}
                            </button>
                            <div className="text-xs text-brand-muted mt-3 text-center max-w-[280px]">
                                {recording ? "Speak the price… (auto-stops in 8s)" :
                                    transcribing ? "Transcribing your voice…" :
                                        'Tap mic, say e.g. "two thousand five hundred" or "do hazaar paanch sau"'}
                            </div>
                        </div>

                        {transcript ? (
                            <div className="ayraa-card p-3 text-xs space-y-2" data-testid="voice-price-result">
                                <div>
                                    <span className="ayraa-overline">Heard</span>
                                    <div className="text-brand-muted italic mt-0.5">"{transcript}"</div>
                                </div>
                                {parsedPrice ? (
                                    <div className="flex items-center gap-2 text-brand-primary">
                                        <Sparkles className="w-3.5 h-3.5" />
                                        <span>Detected ₹{parsedPrice.toLocaleString("en-IN")}</span>
                                    </div>
                                ) : (
                                    <div className="text-brand-soft">No price detected — type it below or try again.</div>
                                )}
                            </div>
                        ) : null}

                        <div>
                            <label className="ayraa-overline">Price (₹)</label>
                            <input
                                type="number"
                                min="0"
                                className="ayraa-input mt-1.5 text-lg"
                                value={manualPrice}
                                onChange={(e) => setManualPrice(e.target.value)}
                                placeholder="e.g. 2500"
                                data-testid="voice-price-input"
                            />
                        </div>

                        <div className="flex items-center justify-between gap-2">
                            <button className="ayraa-btn-ghost text-xs" onClick={goBack} disabled={idx === 0} data-testid="voice-price-prev">
                                <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Back
                            </button>
                            <button
                                className="ayraa-btn-ghost text-xs"
                                onClick={() => { setTranscript(""); setParsedPrice(null); setManualPrice(""); }}
                                data-testid="voice-price-reset"
                            >
                                <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset
                            </button>
                            <button className="ayraa-btn-ghost text-xs" onClick={skip} data-testid="voice-price-skip">
                                Skip <ChevronRight className="w-3.5 h-3.5 ml-1" />
                            </button>
                        </div>

                        <button
                            className="ayraa-btn-primary w-full"
                            onClick={savePrice}
                            disabled={!Number(manualPrice) || saving}
                            data-testid="voice-price-save"
                        >
                            {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</> : <><Check className="w-4 h-4 mr-1.5" /> Save & next</>}
                        </button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
